import { Alert, Button, ConfirmDialog, EmptyState, IconButton, LoadingState, Textarea } from '@orbind/ui'
import { useEffect, useRef, useState, type KeyboardEvent } from 'react'
import { useNavigate, useParams } from 'react-router'
import { CartaoDePergunta } from '../features/ai-assistant/CartaoDePergunta.tsx'
import { MarkdownDoAssistente } from '../features/ai-assistant/markdown.tsx'
import {
  ROTULO_DA_FERRAMENTA,
  useApagarConversa,
  useConversa,
  useConversas,
  useEnviarAoAssistente,
  useSituacaoDoAssistente,
  type FalaDaConversa,
} from '../features/ai-assistant/useAssistente.ts'
import './assistente.css'

/**
 * Orbind AI (SPEC-20261104-2229) — conversa com o Claude DENTRO do Orbind, com
 * as ferramentas, permissões e confirmações do conector.
 *
 * As conversas ficam no servidor, na lista à esquerda (como as sessões do
 * Claude): sair da tela e voltar reabre a última conversa como estava, e se a
 * resposta ainda não chegou ela aparece sozinha quando chegar.
 *
 * Estados: carregando; erro; fora do plano; sem liberação do administrador;
 * servidor sem chave; conversa nova (sugestões); conversa; pensando; erro de
 * uma resposta (o texto volta para a caixa, nada se perde); pergunta de
 * decisão em aberto (cartão com opções; escrever na caixa também responde).
 */

/** O Textarea do @orbind/ui não repassa ref: a caixa é achada pelo id. */
const ID_DA_CAIXA = 'assistente-mensagem'
const focarCaixa = () => document.getElementById(ID_DA_CAIXA)?.focus()

/** Última conversa aberta, para voltar a ela (conveniência deste navegador). */
const CHAVE_DA_ULTIMA = 'orbind.orbind-ai.ultima-conversa'
const lerUltima = () => {
  try {
    return localStorage.getItem(CHAVE_DA_ULTIMA)
  } catch {
    return null
  }
}
const gravarUltima = (id: string | null) => {
  try {
    if (id) localStorage.setItem(CHAVE_DA_ULTIMA, id)
    else localStorage.removeItem(CHAVE_DA_ULTIMA)
  } catch {
    /* navegador sem armazenamento: só não lembra */
  }
}

const NOVA = 'nova'

const SUGESTOES = [
  'O que eu tenho para hoje?',
  'Quais Itens meus estão atrasados?',
  'Resuma o andamento dos Itens que acompanho.',
  'Crie um Item a partir desta ideia: …',
]

const quando = (iso: string) => {
  const d = new Date(iso)
  const hoje = new Date()
  return d.toDateString() === hoje.toDateString()
    ? d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
    : d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })
}

export function AssistentePage() {
  const navigate = useNavigate()
  const { conversaId: param } = useParams()
  const situacao = useSituacaoDoAssistente()
  const pronto = !!situacao.data?.noPlano && !!situacao.data.liberado
  const lista = useConversas(pronto)
  const conversaId = param && param !== NOVA ? param : null
  const conversa = useConversa(conversaId)
  const enviar = useEnviarAoAssistente()
  const apagar = useApagarConversa()
  const [rascunho, setRascunho] = useState('')
  const [erro, setErro] = useState<string | null>(null)
  /** A fala acabou de ser enviada e o servidor ainda não respondeu. */
  const [enviada, setEnviada] = useState<string | null>(null)
  const [apagando, setApagando] = useState<string | null>(null)
  const [listaAberta, setListaAberta] = useState(false)
  const fimRef = useRef<HTMLDivElement>(null)

  // /assistant sem conversa: volta para a última aberta, se ainda existir
  useEffect(() => {
    if (param || !lista.data) return
    const ultima = lerUltima()
    if (ultima && lista.data.some((c) => c.id === ultima)) navigate(`/assistant/${ultima}`, { replace: true })
  }, [param, lista.data, navigate])

  useEffect(() => {
    if (conversaId) gravarUltima(conversaId)
  }, [conversaId])

  // conversa apagada (ou de outra pessoa): volta para uma nova
  useEffect(() => {
    if (conversa.isError && conversaId) {
      gravarUltima(null)
      navigate(`/assistant/${NOVA}`, { replace: true })
    }
  }, [conversa.isError, conversaId, navigate])

  const falasDoServidor: FalaDaConversa[] = conversaId ? (conversa.data?.falas ?? []) : []
  const falas: FalaDaConversa[] = enviada
    ? [...falasDoServidor, { quem: 'pessoa', texto: enviada, em: new Date().toISOString() }]
    : falasDoServidor
  const pensando = enviar.isPending || !!conversa.data?.pensando
  const ultima = falasDoServidor.at(-1)
  const emAberto = ultima?.quem === 'assistente' && ultima.pergunta && ultima.respondida === undefined ? ultima.pergunta : null

  useEffect(() => {
    fimRef.current?.scrollIntoView({ block: 'end', behavior: 'smooth' })
  }, [falas.length, pensando])

  const mandar = (texto: string, digitada = true) => {
    const mensagem = texto.trim()
    if (!mensagem || pensando) return
    setErro(null)
    if (digitada) setRascunho('')
    setEnviada(mensagem)
    enviar.mutate(
      {
        ...(conversaId ? { conversa: conversaId } : {}),
        ...(emAberto ? { resposta: { pergunta: emAberto.id, resposta: mensagem } } : { mensagem }),
      },
      {
        onSuccess: (c) => {
          if (c.id !== conversaId) navigate(`/assistant/${c.id}`, { replace: !conversaId })
        },
        onError: (e) => {
          // a fala volta para a caixa: a pessoa reenvia sem redigitar
          if (digitada) setRascunho(mensagem)
          setErro(e instanceof Error ? e.message : 'O Orbind AI não conseguiu responder agora.')
          void conversa.refetch()
        },
        onSettled: () => {
          setEnviada(null)
          focarCaixa()
        },
      },
    )
  }

  const novaConversa = () => {
    setErro(null)
    setRascunho('')
    setListaAberta(false)
    navigate(`/assistant/${NOVA}`)
    focarCaixa()
  }

  const abrir = (id: string) => {
    setErro(null)
    setListaAberta(false)
    navigate(`/assistant/${id}`)
  }

  const aoTeclar = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault()
      mandar(rascunho)
    }
  }

  if (situacao.isPending) {
    return (
      <div className="ast ast--centro">
        <LoadingState variant="detail" label="Carregando o Orbind AI" />
      </div>
    )
  }
  if (situacao.isError) {
    return (
      <div className="ast ast--centro">
        <Alert tone="danger" title="Não foi possível abrir o Orbind AI">
          {situacao.error.message}
          <div className="ast__acoes-do-aviso">
            <Button size="sm" onClick={() => void situacao.refetch()}>
              Tentar de novo
            </Button>
          </div>
        </Alert>
      </div>
    )
  }

  const s = situacao.data
  if (!s.noPlano || !s.liberado || !s.configurado) {
    const [titulo, descricao] = !s.noPlano
      ? ['O Orbind AI não está no seu plano', 'Ele vem junto com os Conectores de IA. Fale com quem administra a conta.']
      : !s.liberado
        ? [
            'A IA ainda não foi liberada para você',
            'Quem administra o workspace libera na Administração, em Conectores de IA — lá também define o que a IA pode fazer por você.',
          ]
        : ['O Orbind AI ainda não foi configurado', 'Falta a chave da Anthropic. O super admin cadastra em Console > Orbind AI.']
    return (
      <div className="ast ast--centro">
        <EmptyState icon="fas fa-lock" title={titulo} description={descricao} />
      </div>
    )
  }

  const soLeitura = s.permissoes.every((p) => p === 'consultar')
  const titulo = conversaId ? (conversa.data?.titulo ?? 'Conversa') : 'Nova conversa'
  const carregandoConversa = conversaId !== null && conversa.isPending

  return (
    <div className={`ast-shell${listaAberta ? ' ast-shell--lista' : ''}`}>
      <aside className="ast-lista" aria-label="Conversas do Orbind AI">
        <div className="ast-lista__topo">
          <span className="ast-lista__titulo">
            <i className="fas fa-magic" aria-hidden /> Orbind AI
          </span>
          <Button size="sm" variant="primary" icon="fas fa-plus" onClick={novaConversa}>
            Nova
          </Button>
        </div>
        {lista.isPending ? (
          <LoadingState variant="list" label="Carregando as conversas" />
        ) : lista.isError ? (
          <div className="ast-lista__aviso">
            Não foi possível carregar as conversas.{' '}
            <button type="button" className="ast__link" onClick={() => void lista.refetch()}>
              Tentar de novo
            </button>
          </div>
        ) : lista.data.length === 0 ? (
          <p className="ast-lista__aviso">As conversas ficam guardadas aqui. Comece uma ao lado.</p>
        ) : (
          <ul className="ast-lista__itens">
            {lista.data.map((c) => (
              <li key={c.id} className={`ast-lista__item${c.id === conversaId ? ' ast-lista__item--on' : ''}`}>
                <button
                  type="button"
                  className="ast-lista__abrir"
                  aria-current={c.id === conversaId ? 'page' : undefined}
                  onClick={() => abrir(c.id)}
                >
                  <span className="ast-lista__nome">{c.titulo}</span>
                  <span className="ast-lista__meta">
                    {c.pensando ? (
                      <>
                        <i className="fas fa-circle-notch fa-spin" aria-hidden /> respondendo
                      </>
                    ) : (
                      quando(c.atualizadaEm)
                    )}
                  </span>
                </button>
                <IconButton
                  size="sm"
                  icon="far fa-trash-alt"
                  label={`Apagar a conversa "${c.titulo}"`}
                  className="ast-lista__apagar"
                  onClick={() => setApagando(c.id)}
                />
              </li>
            ))}
          </ul>
        )}
      </aside>

      <div className="ast">
        <header className="ast__topo">
          <IconButton
            icon="fas fa-bars"
            label="Mostrar as conversas"
            className="ast__mostrar-lista"
            onClick={() => setListaAberta((v) => !v)}
          />
          <h1 className="ast__titulo" title={titulo}>
            {titulo}
          </h1>
          <span className="ast__permissoes">{soLeitura ? 'Somente consulta' : `Pode: ${s.permissoes.join(', ')}`}</span>
        </header>

        <div className="ast__conversa" aria-live="polite">
          {carregandoConversa ? (
            <LoadingState variant="detail" label="Abrindo a conversa" />
          ) : falas.length === 0 ? (
            <div className="ast__vazio">
              <EmptyState
                icon="fas fa-magic"
                title="Como posso ajudar?"
                description="Pergunte sobre os seus Itens de Trabalho, peça um resumo do dia ou para criar, comentar e mover Itens. Antes de mudar qualquer coisa eu confirmo com você."
              />
              <div className="ast__sugestoes">
                {SUGESTOES.map((t) => (
                  <button
                    key={t}
                    type="button"
                    className="ast__sugestao"
                    onClick={() => (t.endsWith('…') ? (setRascunho(t.slice(0, -1)), focarCaixa()) : mandar(t))}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            falas.map((f, i) => (
              <div key={i} className={`ast__fala ast__fala--${f.quem}`}>
                {f.texto ? (
                  <div className="ast__balao">
                    {f.quem === 'assistente' ? <MarkdownDoAssistente texto={f.texto} /> : <p>{f.texto}</p>}
                  </div>
                ) : null}
                {f.pergunta ? (
                  <CartaoDePergunta
                    pergunta={f.pergunta}
                    respondida={f.respondida ?? (i < falas.length - 1 ? '' : undefined)}
                    ocupado={pensando}
                    onResponder={(resposta) => mandar(resposta, false)}
                  />
                ) : null}
                {f.acoes?.length ? (
                  <div className="ast__feito">
                    <i className="fas fa-tools" aria-hidden />
                    {[...new Set(f.acoes.map((a) => ROTULO_DA_FERRAMENTA[a.ferramenta] ?? a.ferramenta))].join(' · ')}
                  </div>
                ) : null}
              </div>
            ))
          )}
          {pensando ? (
            <div className="ast__fala ast__fala--assistente">
              <div className="ast__balao ast__balao--pensando" role="status">
                <span className="ast__pontos" aria-hidden>
                  <i />
                  <i />
                  <i />
                </span>
                Pensando…
              </div>
            </div>
          ) : null}
          <div ref={fimRef} />
        </div>

        <footer className="ast__rodape">
          {erro ? (
            <Alert tone="danger" title="A resposta não veio">
              {erro}
            </Alert>
          ) : null}
          <div className="ast__caixa">
            <Textarea
              id={ID_DA_CAIXA}
              rows={2}
              value={rascunho}
              placeholder={
                emAberto
                  ? 'Escolha uma opção acima ou escreva outra resposta'
                  : 'Escreva para o Orbind AI — Enter envia, Shift+Enter quebra a linha'
              }
              aria-label="Mensagem para o Orbind AI"
              onChange={(e) => setRascunho(e.target.value)}
              onKeyDown={aoTeclar}
              maxLength={8000}
            />
            <Button
              variant="primary"
              icon="fas fa-paper-plane"
              loading={enviar.isPending}
              disabled={!rascunho.trim() || pensando}
              onClick={() => mandar(rascunho)}
            >
              Enviar
            </Button>
          </div>
          <p className="ast__nota">
            As respostas são geradas por IA e podem errar. O que for feito fica no histórico do Item e na{' '}
            <button type="button" className="ast__link" onClick={() => navigate('/claude')}>
              sua lista de ações da IA
            </button>
            .
          </p>
        </footer>
      </div>

      <ConfirmDialog
        open={apagando !== null}
        title="Apagar esta conversa?"
        description="A conversa some da sua lista e não pode ser recuperada. O que o Orbind AI já fez nos Itens continua lá."
        confirmLabel="Apagar conversa"
        destructive
        loading={apagar.isPending}
        onCancel={() => setApagando(null)}
        onConfirm={() => {
          const id = apagando
          if (!id) return
          apagar.mutate(id, {
            onSuccess: () => {
              setApagando(null)
              if (id === conversaId) navigate(`/assistant/${NOVA}`, { replace: true })
            },
            onError: (e) => {
              setApagando(null)
              setErro(e instanceof Error ? e.message : 'Não foi possível apagar a conversa.')
            },
          })
        }}
      />
    </div>
  )
}
